
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Search, 
  Award, 
  User, 
  AlertTriangle,
  CheckCircle,
  Filter
} from 'lucide-react';
import { AdminCertificateService } from '@/services/adminCertificateService';
import { toast } from '@/components/ui/sonner';

interface PendingCertificate {
  userId: string;
  userName: string;
  courseId: string;
  courseTitle: string;
  completionDate: string;
  quizAverage: number;
  canIssue: boolean;
  reason?: string;
}

interface PendingCertificatesListProps {
  certificates: PendingCertificate[];
  onRefresh: () => void;
}

const PendingCertificatesList: React.FC<PendingCertificatesListProps> = ({
  certificates,
  onRefresh
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'ready' | 'pending'>('all');
  const [generating, setGenerating] = useState<string[]>([]);

  const filteredCertificates = certificates.filter(cert => {
    const matchesSearch = cert.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         cert.courseTitle.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'ready' && cert.canIssue) ||
                         (filterStatus === 'pending' && !cert.canIssue);
    return matchesSearch && matchesFilter;
  });

  const handleGenerateSingle = async (certificate: PendingCertificate) => {
    setGenerating(prev => [...prev, certificate.userId]);
    
    try {
      const result = await AdminCertificateService.bulkGenerateCertificates([certificate]);
      
      if (result.success > 0) {
        toast.success(`Certificate generated for ${certificate.userName}`);
        onRefresh();
      } else {
        toast.error(`Failed to generate certificate: ${result.errors[0] || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error generating certificate:', error);
      toast.error('Failed to generate certificate');
    } finally {
      setGenerating(prev => prev.filter(id => id !== certificate.userId));
    }
  };

  return (
    <div className="space-y-4">
      {/* Search and Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by user name or course..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="border rounded-md px-3 py-2 text-sm"
              >
                <option value="all">All Certificates</option>
                <option value="ready">Ready to Issue</option>
                <option value="pending">Pending Requirements</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Certificates List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredCertificates.map(certificate => (
          <Card key={certificate.userId} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="h-4 w-4" />
                    {certificate.userName}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">{certificate.courseTitle}</p>
                </div>
                <Badge variant={certificate.canIssue ? "default" : "secondary"}>
                  {certificate.canIssue ? "Ready" : "Pending"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Quiz Average:</span>
                  <span className="font-medium">{certificate.quizAverage.toFixed(1)}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Completed:</span>
                  <span>{new Date(certificate.completionDate).toLocaleDateString()}</span>
                </div>
                {!certificate.canIssue && certificate.reason && (
                  <div className="flex items-start gap-2 p-2 bg-orange-50 rounded text-sm">
                    <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
                    <span className="text-orange-800">{certificate.reason}</span>
                  </div>
                )}
              </div>

              <Button
                onClick={() => handleGenerateSingle(certificate)}
                disabled={!certificate.canIssue || generating.includes(certificate.userId)}
                className="w-full"
                size="sm"
              >
                {generating.includes(certificate.userId) ? (
                  <>Generating...</>
                ) : certificate.canIssue ? (
                  <>
                    <Award className="h-3 w-3 mr-1" />
                    Generate Certificate
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Requirements Not Met
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredCertificates.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Award className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium mb-2">No pending certificates found</h3>
            <p className="text-muted-foreground">
              {searchQuery || filterStatus !== 'all' 
                ? "No certificates match your search criteria."
                : "All eligible certificates have been generated."
              }
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PendingCertificatesList;
